import { formatInt, formatMoney, formatMonth, formatDate, registerAuditLabels } from "@vixio/ui";

export type KpiFormat = "int" | "decimal" | "money" | "percent" | "hours" | "usd_micros";
export type Period = "week" | "month" | "quarter";

export const KINDS: Record<string, string> = { counter: "Contador de eventos", view: "Fonte de dados", manual: "Lançamento manual", ratio: "Razão entre indicadores" };
export const FORMATS: Record<KpiFormat, string> = { int: "Número", decimal: "Decimal", money: "Reais", percent: "Percentual", hours: "Horas", usd_micros: "Dólares (IA)" };
export const PERIODS: Record<Period, string> = { week: "Semana", month: "Mês", quarter: "Trimestre" };
export const OPS: Record<string, string> = { lt: "abaixo de", lte: "até", gt: "acima de", gte: "a partir de" };
export const AGGREGATES: Record<string, string> = { sum: "Soma", count: "Contagem", avg: "Média", min: "Mínimo", max: "Máximo", last: "Último" };

export function formatKpi(v: number | string | null | undefined, fmt: string): string {
  if (v === null || v === undefined || v === "") return "sem dado";
  const n = Number(v);
  switch (fmt) {
    case "money": return formatMoney(Math.round(n));
    case "percent": return `${(n * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
    case "hours": return `${n.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;
    case "usd_micros": return `US$ ${(n / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "decimal": return n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
    default: return formatInt(n);
  }
}

export function periodLabel(start: string, period: string): string {
  if (period === "week") return `sem. ${formatDate(start).slice(0, 5)}`;
  if (period === "quarter") return `${Math.floor(Number(start.slice(5, 7)) / 3) + 1}º tri ${start.slice(2, 4)}`;
  return formatMonth(start);
}

export interface KpiView {
  id: string;
  org_id: string;
  key: string;
  label: string;
  description: string | null;
  kind: "counter" | "view" | "manual" | "ratio";
  counter_key: string | null;
  source_key: string | null;
  value_column: string | null;
  aggregate: string;
  numerator_key: string | null;
  denominator_key: string | null;
  format: KpiFormat;
  period: Period;
  direction: "up" | "down";
  owner_person_id: string | null;
  owner_name: string | null;
  limit_op: string | null;
  limit_value: number | null;
  action_text: string | null;
  source_ref: string | null;
  position: number;
  active: boolean;
  period_start: string;
  period_label: string;
  value: number | null;
  value_text: string;
  target: number | null;
  target_pct: number | null;
  limit_breached: boolean;
  previous_value: number | null;
}

export interface Source { key: string; module: string; label: string; description: string | null; value_columns: string[]; filter_columns: string[] }
export interface CounterRule { id: string; event_type: string; counter_key: string; label: string; amount_path: string | null }
export interface Dashboard { id: string; org_id: string; key: string; title: string; description: string | null; min_role: string; share_expires_at: string | null; position: number; active: boolean }
export interface Schedule { id: string; dashboard_id: string; title: string; frequency: "weekly" | "monthly"; weekday: number | null; day_of_month: number | null; hour: number; recipients: string[]; next_run_at: string; last_run_at: string | null; active: boolean }
export interface ManualValue { id: string; kpi_id: string; occurred_on: string; value: number; note: string | null; created_at: string }

export interface WidgetState {
  kpi_id: string;
  key: string;
  label: string;
  format: KpiFormat;
  period: Period;
  direction: "up" | "down";
  period_start: string;
  period_label: string;
  value: number | null;
  value_text: string;
  target: number | null;
  target_text: string | null;
  target_pct: number | null;
  limit_op: string | null;
  limit_value: number | null;
  limit_breached: boolean;
  action?: string | null;
  owner?: string | null;
}

export interface WidgetData {
  id: string;
  kind: "number" | "line" | "bar" | "meter";
  span: 1 | 2 | 3;
  periods: number;
  title: string;
  state: WidgetState;
  series: Array<{ period_start: string; value: number | null; target: number | null; closed: boolean }>;
}

export interface DashboardData { id: string; key: string; title: string; description: string | null; generated_at: string; widgets: WidgetData[]; organization?: string; expires_at?: string }

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "analytics.dashboard_data": { args: { p_dashboard: U }; returns: DashboardData };
    "analytics.shared_dashboard": { args: { p_token: string }; returns: DashboardData };
    "analytics.upsert_kpi": {
      args: {
        p_org: U; p_key: string; p_label: string; p_kind: string; p_counter_key?: string | null; p_source_key?: string | null; p_value_column?: string | null;
        p_aggregate?: string; p_format?: string; p_period?: string; p_numerator_key?: string | null; p_denominator_key?: string | null;
        p_limit_op?: string | null; p_limit_value?: number | null; p_action_text?: string | null; p_owner_person?: U | null; p_owner_label?: string | null;
        p_description?: string | null; p_direction?: string; p_filter_column?: string | null; p_filter_value?: string | null; p_source_ref?: string | null;
        p_position?: number; p_active?: boolean;
      };
      returns: U;
    };
    "analytics.add_manual_value": { args: { p_kpi: U; p_occurred_on: string; p_value: number; p_note?: string | null }; returns: U };
    "analytics.set_target": { args: { p_kpi: U; p_date: string | null; p_target: number | null; p_note?: string | null }; returns: string };
    "analytics.take_snapshots": { args: { p_date?: string | null; p_org?: U | null }; returns: number };
    "analytics.upsert_dashboard": { args: { p_org: U; p_key: string; p_title: string; p_description?: string | null; p_min_role?: string; p_position?: number; p_active?: boolean }; returns: U };
    "analytics.upsert_widget": { args: { p_dashboard: U; p_kpi_key: string; p_kind?: string; p_span?: number; p_periods?: number; p_position?: number; p_title?: string | null; p_id?: U | null }; returns: U };
    "analytics.remove_widget": { args: { p_widget: U }; returns: null };
    "analytics.share_dashboard": { args: { p_dashboard: U; p_days?: number }; returns: string };
    "analytics.revoke_share": { args: { p_dashboard: U }; returns: null };
    "analytics.upsert_schedule": {
      args: { p_org: U; p_dashboard: U; p_title: string; p_frequency: string; p_recipients: string[]; p_weekday?: number | null; p_day_of_month?: number | null; p_hour?: number; p_active?: boolean; p_id?: U | null };
      returns: U;
    };
    "analytics.send_report_now": { args: { p_schedule: U }; returns: number };
    "analytics.install_defaults": { args: { p_org: U; p_report_email?: string | null }; returns: null };
  }
}

registerAuditLabels({
  "analytics.kpi_upserted": "Indicador salvo",
  "analytics.manual_value_added": "Valor de indicador lançado",
  "analytics.target_set": "Meta de indicador definida",
  "analytics.dashboard_upserted": "Painel salvo",
  "analytics.dashboard_shared": "Painel compartilhado por link",
  "analytics.dashboard_share_revoked": "Link do painel revogado",
  "analytics.schedule_upserted": "Relatório agendado",
  "analytics.report_sent": "Relatório enviado",
  "analytics.defaults_installed": "Indicadores do PM3 instalados",
});
